/**
 * Where the stage gets its pixels.
 *
 * One interface, so the compositor never learns how frames are produced:
 *
 *   acquire(url) -> handle { ready, seek, play, pause, drawTo, width, height }
 *
 * The implementation here is a pool of `<video>` elements. It needs no
 * dependencies and works in every browser, and it is honest about its one
 * weakness: `currentTime` seeks land on or near a keyframe, so scrubbing is
 * approximate on sparsely-keyframed footage. A WebCodecs implementation
 * (demuxer + VideoDecoder + an LRU of VideoFrames) would seek exactly, and can
 * be dropped in behind this same interface if scrubbing proves too coarse —
 * that is the only reason the seam exists.
 */

/** Seek tolerance during playback. Below this the element is close enough that
 *  forcing currentTime would stutter more than the drift it corrects. */
const SYNC_TOLERANCE = 0.35;

function createHandle(url) {
    const video = document.createElement('video');
    video.src = url;
    // A clip's own audio is part of the edit, so the element that decodes it is
    // also the element that sounds it. This was muted on the assumption that
    // sound arrives from a separate bed on the audio track -- true for music,
    // false for every cut assembled from source footage, which is most of them.
    // Held muted until it is the clip actually playing, so acquiring a handle
    // (or scrubbing across a boundary) never blurts a frame of the wrong audio.
    video.muted = true;
    video.playsInline = true;
    video.preload = 'auto';
    // Deliberately NO crossOrigin. Setting it to 'anonymous' makes the browser
    // refuse the media outright unless the bucket's CORS policy names the exact
    // origin being served from -- and the prod bucket lists only usevixo.com, so
    // the stage went black on localhost and on *.web.app with no error anywhere.
    // The cost of omitting it is a tainted canvas, which costs us nothing: the
    // stage only ever draws, and export happens server-side in ffmpeg. If a
    // "save this frame" feature ever lands, it needs the CORS entry first.

    // Load failures are recorded rather than left to an unhandled rejection --
    // nothing awaits `ready`, so a rejection here was swallowed and the only
    // symptom was a black stage.
    let failure = null;
    let blocked = false;

    const ready = new Promise((resolve, reject) => {
        const onLoaded = () => resolve({
            width: video.videoWidth,
            height: video.videoHeight,
            duration: video.duration || 0,
        });
        if (video.readyState >= 1) { onLoaded(); return; }
        video.addEventListener('loadedmetadata', onLoaded, { once: true });
        video.addEventListener('error', () => {
            failure = video.error?.message || 'Could not load media';
            reject(new Error(failure));
        }, { once: true });
    });
    ready.catch(() => { /* surfaced via handle.failure instead */ });

    return {
        url,
        video,
        ready,
        get failure() { return failure; },
        get blocked() { return blocked; },
        get width() { return video.videoWidth; },
        get height() { return video.videoHeight; },
        get duration() { return video.duration || 0; },

        seek(seconds) {
            const target = Math.max(0, seconds);
            if (Math.abs((video.currentTime || 0) - target) < 0.01) return;
            try { video.currentTime = target; } catch { /* not seekable yet */ }
        },

        /** Nudge back into sync only when the drift is visible, so playback is
         *  left to the element's own clock the rest of the time. */
        syncTo(seconds) {
            if (Math.abs((video.currentTime || 0) - seconds) > SYNC_TOLERANCE) {
                this.seek(seconds);
            }
        },

        /** Audible only while it is the clip being played. */
        setMuted(muted) {
            if (video.muted !== muted) video.muted = muted;
        },

        /** Preview the clip's own level. A media element cannot go above 1, so a
         *  boost is previewed at full rather than not at all; the render is
         *  where the extra gain actually happens. */
        setVolume(level) {
            const clamped = Math.max(0, Math.min(1, Number(level)));
            if (!Number.isFinite(clamped)) return;
            if (video.volume !== clamped) video.volume = clamped;
        },

        play() {
            if (!video.paused) return;
            video.play().catch((err) => {
                // Audible playback needs user activation. Pressing the transport
                // is one, so this should not fire -- but if the browser refuses,
                // say so rather than leaving someone staring at a silent picture.
                if (err?.name === 'NotAllowedError') blocked = true;
            });
        },

        pause() {
            if (!video.paused) video.pause();
        },

        /** Letterbox into the target rect, preserving the source aspect. */
        drawTo(ctx, targetWidth, targetHeight) {
            const { videoWidth: sw, videoHeight: sh } = video;
            if (!sw || !sh) return false;
            const scale = Math.min(targetWidth / sw, targetHeight / sh);
            const width = sw * scale;
            const height = sh * scale;
            ctx.drawImage(
                video,
                (targetWidth - width) / 2,
                (targetHeight - height) / 2,
                width,
                height
            );
            return true;
        },

        /** Fill an exact rect. For an inset, whose rect was already sized to
         *  the source's aspect by the same placement the render uses. */
        drawRect(ctx, x, y, width, height) {
            if (!video.videoWidth || !video.videoHeight) return false;
            ctx.drawImage(video, x, y, width, height);
            return true;
        },

        release() {
            video.pause();
            video.removeAttribute('src');
            video.load();
        },
    };
}

export function createFrameSource() {
    const handles = new Map();
    const images = new Map();

    return {
        /** Handles are cached per url: re-creating an element on every playhead
         *  move would re-download and re-buffer the clip continuously.
         *
         *  `slot` separates uses of one file that must play at different
         *  positions at once -- the same footage as the main clip and as an
         *  inset over it. One element cannot be in two places. The returned
         *  handle's `key` is what pauseAllExcept is told to keep. */
        acquire(url, slot = 'main') {
            if (!url) return null;
            const key = slot === 'main' ? url : `${slot}|${url}`;
            if (!handles.has(key)) {
                // Assigned, not spread: the handle's getters must stay live.
                const handle = createHandle(url);
                handle.key = key;
                handles.set(key, handle);
            }
            return handles.get(key);
        },

        /** A still needs no seeking or decoding — just the decoded bitmap, held
         *  for as long as its clip lasts. */
        acquireImage(url) {
            if (!url) return null;
            if (!images.has(url)) {
                const image = new Image();
                image.crossOrigin = 'anonymous';
                image.src = url;
                images.set(url, image);
            }
            return images.get(url);
        },

        /** Everything that is not the clip being played goes quiet as well as
         *  still: a paused element that is later resumed mid-seek should not
         *  come back audible behind the clip that replaced it. */
        pauseAllExcept(keep) {
            // One key (the main clip) or several (the main clip and its insets).
            const kept = new Set(Array.isArray(keep) ? keep : [keep]);
            handles.forEach((handle, key) => {
                if (kept.has(key)) return;
                handle.pause();
                handle.setMuted(true);
            });
        },

        releaseAll() {
            handles.forEach(handle => handle.release());
            handles.clear();
            images.clear();
        },
    };
}

/**
 * The real length of a media file, read from its own metadata.
 *
 * Asset records do not reliably carry a duration -- the field is variously
 * `Duration`, `duration`, `T`, `t` or simply absent -- and when it was absent a
 * dropped clip silently became a 4-second stub of a two-minute video. The file
 * always knows how long it is, so ask it. A `<video>` element reads audio
 * metadata perfectly well, so one probe covers both tracks.
 *
 * Resolves to null rather than rejecting: a probe that fails should fall back to
 * the default hold, not sink the drop.
 */
export function probeMediaDuration(url, { timeoutMs = 8000 } = {}) {
    return new Promise((resolve) => {
        if (!url || typeof document === 'undefined') { resolve(null); return; }

        const element = document.createElement('video');
        element.preload = 'metadata';
        element.muted = true;
        let settled = false;

        const finish = (value) => {
            if (settled) return;
            settled = true;
            clearTimeout(timer);
            element.removeAttribute('src');
            element.load();          // let the element go rather than leaving it buffering
            resolve(value);
        };

        const timer = setTimeout(() => finish(null), timeoutMs);
        element.addEventListener('loadedmetadata', () => {
            const duration = Number(element.duration);
            finish(Number.isFinite(duration) && duration > 0 ? duration : null);
        }, { once: true });
        element.addEventListener('error', () => finish(null), { once: true });

        element.src = url;
    });
}

/** True when a frame-exact source could be used instead of `<video>`. Reported
 *  so the stage can say which mode it is in, not to branch behaviour today. */
export const supportsFrameExactDecode = () => typeof window !== 'undefined' && 'VideoDecoder' in window;
